import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaLink,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaClock,
  FaExclamationTriangle,
  FaCopy,
  FaPlay
} from 'react-icons/fa';
import { paymentService } from '../../../../services/paymentService';
import { toast } from 'react-toastify';
import { formatPrice } from '../../../../utils/currency';

const WebhookManagement = () => {
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [testing, setTesting] = useState({});
  const [activeTab, setActiveTab] = useState('webhooks'); // 'webhooks' or 'logs'

  useEffect(() => {
    fetchWebhooks();
    fetchLogs();
  }, []);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getWebhookConfig();
      setWebhooks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
      if (error.message?.includes('404') || error.message?.includes('Failed to fetch')) {
        toast.error('Payment webhook routes not available. Please ensure the backend server is restarted.');
      } else {
        toast.error('Failed to load webhook configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const data = await paymentService.getWebhookLogs({});
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch webhook logs:', error);
      if (error.message?.includes('404') || error.message?.includes('Failed to fetch')) {
        toast.error('Webhook logs endpoint not available. Please ensure the backend server is restarted.');
      }
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      setTesting(prev => ({ ...prev, [webhookId]: true }));
      const result = await paymentService.testWebhook(webhookId);
      toast.success('Webhook test successful!');
      fetchLogs(); // Refresh logs
    } catch (error) {
      toast.error('Webhook test failed: ' + error.message);
    } finally {
      setTesting(prev => ({ ...prev, [webhookId]: false }));
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { bg: 'bg-green-100', text: 'text-green-800', icon: FaCheckCircle },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: FaTimesCircle },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FaClock },
      error: { bg: 'bg-red-100', text: 'text-red-800', icon: FaExclamationTriangle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="text-xs" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Webhook Management</h2>
          <p className="text-sm text-gray-600 mt-1">Manage payment gateway webhooks and monitor logs</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FaPlus /> Add Webhook
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('webhooks')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'webhooks'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Webhooks ({webhooks.length})
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'logs'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Logs ({logs.length})
          </button>
        </div>
      </div>

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <div className="space-y-4">
          {webhooks.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <FaLink className="text-4xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No webhooks configured</p>
              <p className="text-sm text-gray-500 mt-1">Add a webhook to receive payment notifications</p>
            </div>
          ) : (
            webhooks.map((webhook) => (
              <motion.div
                key={webhook.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                    <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FaLink className="text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{webhook.name}</h3>
                        <p className="text-xs text-gray-500">{webhook.gateway.toUpperCase()} • {webhook.events?.length || 0} events</p>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      <div>
                        <label className="text-xs font-medium text-gray-500">Webhook URL</label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="text"
                            value={webhook.url}
                            readOnly
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                          />
                          <button
                            onClick={() => handleCopyUrl(webhook.url)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Copy URL"
                          >
                            <FaCopy />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500">Status</label>
                          <div className="mt-1">
                            {getStatusBadge(webhook.status || 'active')}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">Last Triggered</label>
                          <p className="text-sm text-gray-700 mt-1">
                            {webhook.lastTriggered ? new Date(webhook.lastTriggered).toLocaleString() : 'Never'}
                          </p>
                        </div>
                      </div>
                      {webhook.events && webhook.events.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-500">Events</label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {webhook.events.map((event, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
                              >
                                {event}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleTestWebhook(webhook.id)}
                      disabled={testing[webhook.id]}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                      title="Test Webhook"
                    >
                      {testing[webhook.id] ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <FaPlay />
                      )}
                      Test
                    </button>
                    <button
                      onClick={() => setSelectedWebhook(webhook)}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      className="flex items-center gap-2 px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No webhook logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{log.gateway}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{log.event}</td>
                      <td className="px-6 py-4">{getStatusBadge(log.status)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {log.responseCode || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          className="text-blue-600 hover:text-blue-900 text-sm"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Webhook Modal */}
      <AnimatePresence>
        {showAddModal && (
          <WebhookModal
            webhook={selectedWebhook}
            onClose={() => {
              setShowAddModal(false);
              setSelectedWebhook(null);
            }}
            onSave={() => {
              fetchWebhooks();
              setShowAddModal(false);
              setSelectedWebhook(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const WebhookModal = ({ webhook, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: webhook?.name || '',
    gateway: webhook?.gateway || 'razorpay',
    url: webhook?.url || '',
    events: webhook?.events || [],
    secret: webhook?.secret || '',
    active: webhook?.active !== false
  });
  const [saving, setSaving] = useState(false);

  const availableEvents = {
    razorpay: ['payment.captured', 'payment.failed', 'refund.created', 'refund.processed'],
    phonepe: ['payment.success', 'payment.failure', 'refund.success']
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await paymentService.updateWebhook(formData);
      toast.success('Webhook saved successfully!');
      onSave();
    } catch (error) {
      toast.error('Failed to save webhook: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {webhook ? 'Edit Webhook' : 'Add New Webhook'}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Webhook name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gateway</label>
              <select
                value={formData.gateway}
                onChange={(e) => setFormData({ ...formData, gateway: e.target.value, events: [] })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="razorpay">Razorpay</option>
                <option value="phonepe">PhonePe</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://your-domain.com/webhook"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
              <div className="space-y-2">
                {availableEvents[formData.gateway]?.map((event) => (
                  <label key={event} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.events.includes(event)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, events: [...formData.events, event] });
                        } else {
                          setFormData({ ...formData, events: formData.events.filter(e => e !== event) });
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{event}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secret (Optional)</label>
              <input
                type="password"
                value={formData.secret}
                onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Webhook secret for verification"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WebhookManagement;

